import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

export const InstallPwaPrompt: React.FC = () => {
  const { language } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isIos, setIsIos] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already running as installed standalone PWA
    const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || 
      (window.navigator as any).standalone === true;
    setIsStandalone(isStandaloneMode);

    // Detect iOS devices (iPhone, iPad, iPod)
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
    setIsIos(isIosDevice);

    // Capture Chrome / Edge / Android install prompt
    const handleBeforeInstall = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted PWA installation');
      }
      setDeferredPrompt(null);
    } else {
      // Show helpful modal for iOS or manual install
      setShowModal(true);
    }
  };

  // If already installed or explicitly dismissed in this session, do not clutter
  if (isStandalone || dismissed) {
    return null;
  }

  return (
    <>
      {/* Floating or Top Install Banner on mobile/desktop */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white px-3.5 py-2.5 rounded-2xl border border-indigo-500/30 shadow-lg flex items-center justify-between gap-3 text-xs font-mono">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center font-black text-slate-950 text-sm shadow-md shrink-0">
            A
          </div>
          <div className="truncate">
            <div className="font-extrabold text-[11px] sm:text-xs text-white flex items-center gap-1.5">
              <span>{language === 'sk' ? 'Mobilná Aplikácia Anogi Traders' : 'Anogi Traders Mobile App'}</span>
              <span className="text-[8px] bg-emerald-500/20 text-emerald-300 border border-emerald-400/40 px-1.5 py-0.2 rounded font-black uppercase">PWA</span>
            </div>
            <div className="text-[9px] text-slate-300 truncate">
              {language === 'sk' ? 'Nainštalujte si aplikáciu priamo na plochu mobilu' : 'Add to Home Screen for fast native experience'}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={handleInstallClick}
            className="bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-slate-950 font-black px-3 py-1.5 rounded-xl text-[10px] sm:text-xs transition-all shadow-md flex items-center gap-1"
          >
            <span>📱</span>
            <span>{language === 'sk' ? 'Inštalovať' : 'Install'}</span>
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="text-slate-400 hover:text-white px-1.5 py-1 text-sm font-bold"
            title="Zavrieť"
          >
            ×
          </button>
        </div>
      </div>

      {/* iOS Installation Instruction Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 text-white rounded-3xl max-w-sm w-full p-6 space-y-4 shadow-2xl font-mono text-xs">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-2xl bg-amber-500 flex items-center justify-center font-black text-slate-950 text-lg">
                  A
                </div>
                <div>
                  <h4 className="font-black text-sm text-white">Inštalácia do mobilu</h4>
                  <p className="text-[10px] text-slate-400">Anogi Traders Web App</p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-white text-lg font-bold"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 pt-2 text-stone-200">
              {isIos ? (
                <>
                  <p className="text-[11px] leading-relaxed">
                    Pre inštaláciu na <strong>iPhone / iPad (Safari)</strong>:
                  </p>
                  <ol className="list-decimal list-inside space-y-2 text-[11px] bg-slate-800/80 p-3.5 rounded-2xl border border-slate-700">
                    <li>Ťuknite na tlačidlo <strong>Zdieľať</strong> (ikona štvorca so šípkou hore <span className="text-base">⎋</span> v dolnej lište Safari).</li>
                    <li>Posuňte ponuku nižšie a zvoľte <strong>„Pridať na plochu“</strong> (Add to Home Screen ⊞).</li>
                    <li>Ťuknite vpravo hore na <strong>„Pridať“</strong>.</li>
                  </ol>
                  <p className="text-[10px] text-emerald-400">
                    ✓ Aplikácia sa objaví na ploche ako samostatná natívna aplikácia s rýchlym prístupom a celou obrazovkou bez lišty prehliadača.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-[11px] leading-relaxed">
                    Pre inštaláciu v <strong>Chrome / Android / PC</strong>:
                  </p>
                  <div className="bg-slate-800/80 p-3.5 rounded-2xl border border-slate-700 space-y-2 text-[11px]">
                    <p>1. Otvorte menu prehliadača (3 bodky ⋮ vpravo hore).</p>
                    <p>2. Zvoľte <strong>„Inštalovať aplikáciu“</strong> alebo <strong>„Pridať na plochu“</strong>.</p>
                  </div>
                  <div className="bg-amber-950/40 p-2.5 rounded-xl border border-amber-800/60 text-[10px] text-amber-200 space-y-1">
                    <p className="font-bold flex items-center gap-1"><span>🛡️</span> <span>Ak Google blokuje inštaláciu (Play Protect):</span></p>
                    <p>Google Play ➔ Profil (vpravo hore) ➔ <strong>Play Protect</strong> ➔ Nastavenia ⚙️ ➔ Vypnite <strong>„Zlepšiť detekciu škodlivých aplikácií“</strong>.</p>
                  </div>
                </>
              )}
            </div>

            <button
              onClick={() => setShowModal(false)}
              className="w-full bg-slate-800 hover:bg-slate-700 text-white font-extrabold py-2.5 rounded-xl border border-slate-600 transition-all text-center mt-2"
            >
              Rozumiem
            </button>
          </div>
        </div>
      )}
    </>
  );
};
